import React, { useEffect, useState } from 'react';
import ModalMobile from '../modal-mobile';
import mapkitToken from '../../helpers/mapkit-token';
import { showDistanceFromYou } from '../../helpers/spot';
import { SPOT_DETAIL_MAP_ZM, SPOT_DETAIL_MAP_ZM_MBL } from '../../consts/spot.consts';
import RenderListingFaqs from '../listing-faqs';
import SniffAppleMaps from '../sniff-apple-maps/map-ssr';
import SvgAnaliticsBar from 'src/assets/svg/SvgAnaliticsBar.svg';
import SvgSafetyFloat from 'src/assets/svg/SvgSafetyFloat.svg';
import SvgPin from 'src/assets/svg/SvgPin.svg';
import './index.scss';

const WATER_AMENITIES = ['Beach', 'LakePond', 'RiverStreamCreek', 'SwimmingPool'];
const WATER_SAFETY_URL = 'https://help.sniffspot.com/article/168-can-i-host-if-i-have-a-pool-or-water';

const Description = ({ dscrpt }) => {
    if (!dscrpt) {
        return '';
    }

    return (
        <>
            <h2 className="snif-m1 snif-semibold mt-3 mb-3">Description</h2>
            <div className="row">
                <div className="col-md-12 h4">
                    <div className="dscrp snif-p">{dscrpt}</div>
                </div>
            </div>
            <div className="my-4">
                <hr />
            </div>
        </>
    );
};

const Cleanliness = ({ isMobile, clean }) => {
    const [SvgSize, setSvgSize] = useState(16);

    useEffect(() => {
        if (!isMobile) {
            setSvgSize(24);
        }
    }, []);

    if (clean === null || clean === undefined) {
        return '';
    }

    return (
        <>
            <h2 className="snif-m1 snif-semibold mt-3 mb-3">Cleanliness</h2>
            <div className="row">
                <div className="col-md-12 h4">
                    <div className="dscrp snif-p">
                        <SvgAnaliticsBar width={SvgSize} height={SvgSize} className="icon" />
                        <span className="snif-semibold">{clean}% of guests</span>
                        {' said this spot was as clean as expected during their visit.'}
                    </div>
                </div>
            </div>
            <hr />
        </>
    );
};

const Hazards = ({ hazards, isMobile, hasWater }) => {
    if (!hazards && !hasWater) {
        return '';
    }

    const [safetyModalOpen, setSafetyModalOpen] = useState(false);
    const toggleSafetyModal = () => setSafetyModalOpen(!safetyModalOpen);
    const handleSafetyClick = (e) => {
        e.preventDefault();
        if (isMobile) {
            window.open(WATER_SAFETY_URL, '_blank');
        } else {
            toggleSafetyModal();
        }
    };

    return (
        <>
            <h2 className="snif-m1 snif-semibold mt-3 mb-2">Hazards</h2>
            <div className="hazards-content">
                {hazards && (
                    <div className="host-note">
                        <h3 className="snif-xs snif-semibold text-muted mb-0">HOST NOTE</h3>
                        <div className="row my-0">
                            <div className="col-md-12 h4">
                                <div className="dscrp snif-p mb-0">{hazards}</div>
                            </div>
                        </div>
                    </div>
                )}

                {hasWater && (
                    <div className="water-safety-notice">
                        <div className="notice-container">
                            <span className="icon">
                                <SvgSafetyFloat />
                            </span>
                            <div className="notice-content">
                                <h4 className="notice-title snif-semibold">Water safety notice</h4>
                                <p className="notice-text">
                                    Make sure to practice dog and human safety around water.{' '}
                                    <a
                                        href={WATER_SAFETY_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-underline"
                                        onClick={handleSafetyClick}
                                    >
                                        Read more
                                    </a>
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <hr />
            <ModalMobile title="Dog water park safety tips" isOpen={safetyModalOpen} toggle={toggleSafetyModal} className="water-safety-modal">
                <iframe src={WATER_SAFETY_URL} title="Dog water park safety tips" width="780px" height="616px" style={{ border: 'none' }} />
            </ModalMobile>
        </>
    );
};

export default function ListingLocation({
    isMobileSSR,
    address,
    latitude,
    longitude,
    dscrpt,
    hazards,
    clean,
    city,
    state,
    distanceFrom,
    venueAmenities,
    isSpotFaqsTest,
    isFaqOpen,
    toggleFaq,
}) {
    const isMobile = isMobileSSR;
    const [isOpen, setIsOpen] = useState(false);
    const [distanceText, setDistanceText] = useState('');
    const numLat = latitude ? parseFloat(latitude) : 0;
    const numLng = longitude ? parseFloat(longitude) : 0;
    const hasWater = venueAmenities?.some((amenity) => WATER_AMENITIES.includes(amenity.component));
    const canShowDetails = dscrpt || hazards || hasWater || clean || isSpotFaqsTest;

    useEffect(() => {
        setDistanceText(showDistanceFromYou(distanceFrom));
    }, []);

    return (
        <>
            {latitude && longitude ? (
                <div>
                    <div className="location-head px-2 px-md-0">
                        <div className="snif-m1 snif-semibold mt-3">Location</div>
                        {(address || (city && state)) && (
                            <div className="snif-p d-flex align-items-center my-2">
                                <SvgPin width={20} height={20} />
                                <p style={{ width: 'calc(100% - 20px)', paddingLeft: '8px' }}>
                                    {`${address ? `${address} ` : ''}${city}, ${state}${distanceText}`}
                                </p>
                            </div>
                        )}
                    </div>
                    <div className="map-container">
                        <div className="row">
                            <div className="col-md-12 map">
                                <SniffAppleMaps
                                    center={{ latitude: numLat, longitude: numLng }}
                                    cameraDistance={isMobile ? SPOT_DETAIL_MAP_ZM_MBL : SPOT_DETAIL_MAP_ZM}
                                    circle
                                    {...{ mapkitToken }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
            {canShowDetails ? (
                <div className="location px-2 px-md-0">
                    <Description {...{ dscrpt, isOpen, setIsOpen }} />
                    <RenderListingFaqs {...{ isSpotFaqsTest, isFaqOpen, toggleFaq }} />
                    <Cleanliness {...{ isMobile, clean }} />
                    <Hazards {...{ hazards, isMobile, hasWater }} />
                </div>
            ) : null}
        </>
    );
}
